// Centralized site data — single source of truth
export const COMPANY = {
  name: "Texan Strong Builds",
  short: "TSB",
  phone: "(512) 781-8851",
  phoneRaw: "+15127818851",
  phoneTel: "tel:+15127818851",
  email: "info@texanstrongbuilds.com",
  emailMailto: "mailto:info@texanstrongbuilds.com",
  gmb: "https://share.google/ynrTOxFM8OFvMmC6Q",
  facebook: "https://www.facebook.com/share/1D1pWDAcAw/",
  instagram: "https://www.instagram.com/texanstrongbuilders?igsh=YWltOWFqNWdqdDRq",
  linkedin: "https://www.linkedin.com/in/texanstrong-builders-235779417?utm_source=share_via&utm_content=profile&utm_medium=member_android",
  youtube: "https://www.youtube.com/@TexanStrongBuidls",
  logo: "/tsb-logo.jpg",
  logoWhite: "/tsb-logo-white.png",
  logoSmall: "/tsb-logo-small.webp",
  logoWhiteSmall: "/tsb-logo-white-small.webp",
  address: "1250 S Capital of Texas Hwy, Building 3, Suite 400-450",
  city: "Austin",
  state: "TX",
  zip: "78746",
  fullAddress: "1250 S Capital of Texas Hwy, Building 3, Suite 400-450, Austin, TX 78746",
  hours: "Mon–Fri 8 AM – 6 PM · Sat 8 AM – 2 PM · Sun Closed",
  hoursList: [
    { day: "Monday", time: "8 AM – 6 PM" },
    { day: "Tuesday", time: "8 AM – 6 PM" },
    { day: "Wednesday", time: "8 AM – 6 PM" },
    { day: "Thursday", time: "8 AM – 6 PM" },
    { day: "Friday", time: "8 AM – 6 PM" },
    { day: "Saturday", time: "8 AM – 2 PM" },
    { day: "Sunday", time: "Closed" },
  ],
};

// Content freshness — bump these dates whenever page copy is revised
export const PAGE_UPDATED = {
  home: "2026-06-12",
  services: "2026-06-10",
  xeriscape: "2026-06-11",
  serviceAreas: "2026-06-08",
  about: "2026-06-05",
  blog: "2026-06-12",
  contact: "2026-06-09",
};

export const formatDate = (iso) =>
  new Date(`${String(iso).slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

export const SITE_URL = process.env.REACT_APP_SITE_URL.replace(/\/+$/, "");

export const webPageLd = ({ name, path, updated, description, type = "WebPage" }) => ({
  "@context": "https://schema.org",
  "@type": type,
  name,
  url: `${SITE_URL}${path}`,
  description,
  datePublished: "2025-08-01",
  dateModified: updated,
  isPartOf: {
    "@type": "WebSite",
    name: "Texan Strong Builds",
    url: SITE_URL,
  },
});

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export const SERVICE_AREAS = [
  "Austin",
  "Round Rock",
  "Cedar Park",
  "Leander",
  "Pflugerville",
  "Georgetown",
  "Hutto",
  "Buda",
  "Kyle",
  "Manor",
  "Lakeway",
  "Bee Cave",
  "Dripping Springs",
  "West Lake Hills",
  "Lago Vista",
  "Spicewood",
  "Wimberley",
  "Manchaca",
  "Del Valle",
  "Sunset Valley",
  "Tarrytown",
  "Westlake",
  "Mueller",
  "East Austin",
  "South Austin",
  "North Austin",
  "Jollyville",
  "Steiner Ranch",
  "Avery Ranch",
  "Circle C Ranch",
  "Brushy Creek",
  "Bartlett",
  "Liberty Hill",
];

export const CONCRETE_SERVICES = [
  { name: "Foundations", desc: "Engineered slab and pier foundations built for Austin clay soil." },
  { name: "Driveways", desc: "Reinforced concrete driveways — broom, stamped, exposed aggregate." },
  { name: "Patios & Slabs", desc: "Backyard patios, BBQ pads, shed slabs. Sharp finishes." },
  { name: "Decorative & Stamped", desc: "Stamped, stained and polished concrete that mimics stone." },
  { name: "Footings", desc: "Code-compliant footings for additions, walls, and structures." },
  { name: "Retaining Walls", desc: "Engineered drainage + reinforcement for Hill Country grades." },
  { name: "Piers", desc: "Drilled piers and pier-and-beam foundation support." },
  { name: "Masonry", desc: "Block, brick, stone veneer, Lueders limestone work." },
  { name: "Parking Lots", desc: "Commercial concrete lots, restriping and reseal." },
  { name: "Sidewalks", desc: "ADA-compliant walks, ramps, and approaches." },
  { name: "Curbs", desc: "Decorative borders, monolithic curbs and roll curbs." },
  { name: "Wheel Stops Installation", desc: "Precast and concrete wheel stops, bolted to spec." },
  { name: "Guardrails", desc: "Vehicle guardrails for commercial and HOA properties." },
  { name: "Pools", desc: "Concrete pool decks, coping, and surround pours." },
  { name: "Pavers", desc: "Concrete, stone and travertine paver installation." },
];

export const LANDSCAPE_SERVICES = [
  { name: "Xeriscape", featured: true, desc: "Drought-tolerant Austin design — natives, drip, mulch, rebate-ready." },
  { name: "Yard Leveling", desc: "Re-grading for drainage, foundation protection." },
  { name: "Clearing", desc: "Cedar, brush and lot clearing — Hill Country specialists." },
  { name: "Plants", desc: "Native and adapted plant selection and installation." },
  { name: "Irrigation", desc: "Drip + smart controllers. Convert spray to drip for rebate." },
  { name: "Stonework", desc: "Flagstone paths, boulders, Lueders limestone features." },
  { name: "Planters", desc: "Built-in concrete and stone planters." },
  { name: "Outdoor Lighting", desc: "Low-voltage LED path, accent and security lighting." },
  { name: "Walkways", desc: "Stone, gravel, decomposed granite walks." },
  { name: "Gravel", desc: "Crushed limestone, river rock, decomposed granite." },
  { name: "Artificial Turf", desc: "Premium pet- and play-rated turf installation." },
  { name: "Sod / Grass Installation", desc: "Bermuda, Zoysia, St. Augustine — fresh sod laid right." },
  { name: "Fire Pits", desc: "Gas and wood fire pits, code-compliant clearances." },
  { name: "Decks", desc: "Composite and cedar decks tied to concrete footings." },
  { name: "Pavers (Landscape)", desc: "Patio, walkway and driveway paver systems." },
];

export const TRUST_STATS = [
  { label: "Years Building Austin", value: "10+" },
  { label: "Projects Completed", value: "500+" },
  { label: "Bonded & Insured", value: "TX" },
  { label: "Free Estimates", value: "24h" },
];

export const REVIEWS = [
  {
    name: "Marcus R.",
    area: "Westlake, TX",
    quote: "TSB poured a 600 sq ft stamped patio and rebuilt our retaining wall in under two weeks. Crew was professional, jobsite spotless every night. Best concrete contractor we've used in Austin.",
    rating: 5,
  },
  {
    name: "Priya S.",
    area: "Mueller, Austin",
    quote: "We converted our entire front yard to xeriscape with TSB. Drip system, native plants, decomposed granite paths — and they handled the Austin Water rebate paperwork for us. Water bill dropped 58%.",
    rating: 5,
  },
  {
    name: "Daniel K.",
    area: "Round Rock, TX",
    quote: "Got 4 driveway bids. TSB wasn't the cheapest but they were the only one who explained the 5\" reinforced slab and clay sub-base. Two years later, zero cracks. Worth every dollar.",
    rating: 5,
  },
  {
    name: "Elena V.",
    area: "Dripping Springs, TX",
    quote: "Built an outdoor room with stamped concrete, fire pit and xeriscape borders. Our backyard finally feels like the Hill Country resort we wanted. Highly recommend.",
    rating: 5,
  },
];

export const FAQS = [
  {
    q: "How much does a concrete patio cost in Austin?",
    a: "A standard broom-finish concrete patio in Austin runs $8–$12 per sq ft installed. Stamped or decorative finishes range $14–$22 per sq ft. A typical 400 sq ft stamped patio lands around $5,600–$8,800 with full site prep, reinforcement and sealing.",
  },
  {
    q: "Do you offer free estimates for concrete and landscape work?",
    a: "Yes — every estimate at Texan Strong Builds is free and in writing. We typically visit your property within 24–48 hours of your call.",
  },
  {
    q: "What areas around Austin do you serve?",
    a: "Texan Strong Builds serves Austin, TX and every surrounding suburb including Round Rock, Cedar Park, Leander, Pflugerville, Georgetown, Hutto, Buda, Kyle, Manor, Lakeway, Bee Cave, Dripping Springs, West Lake Hills, Lago Vista, Spicewood, Wimberley and most Hill Country neighborhoods.",
  },
  {
    q: "What is xeriscape and why is it popular in Austin?",
    a: "Xeriscape is a landscape design philosophy that uses drought-tolerant plants, efficient irrigation, mulch and reduced turf to dramatically lower water use. In Austin, xeriscape cuts water bills 50–60% and qualifies for Austin Water rebates up to $3,000.",
  },
  {
    q: "Are you bonded and insured?",
    a: "Yes — Texan Strong Builds is fully bonded and insured in the State of Texas. We provide a certificate of insurance on every project, naming you as additional insured.",
  },
  {
    q: "How long does a typical concrete project take?",
    a: "A standard residential concrete driveway or patio in Austin is prepped, poured and finished by Texan Strong Builds within 5–10 working days. Stamped and decorative finishes add 2–3 days for sealing and curing.",
  },
  {
    q: "Do you pull permits with the City of Austin?",
    a: "Yes — Texan Strong Builds handles all permit submittals, engineering stamps and inspections required by the City of Austin and surrounding municipalities.",
  },
];

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1759369484704-fefd537878f1?w=1900&q=75&auto=format",
  concreteWorker: "https://customer-assets.emergentagent.com/job_xeriscape-austin-tx/artifacts/gyumxcy5_IMG_0217.jpeg",
  stamped: "https://images.unsplash.com/photo-1678794792766-84eb99fe3010?w=1400&q=75&auto=format",
  xeriscape1: "https://images.unsplash.com/photo-1769881271035-518ca09997ce?w=900&q=75&auto=format",
  xeriscape2: "https://images.unsplash.com/photo-1779111718177-aa0f7a30fd62?w=900&q=75&auto=format",
  landscape: "https://images.unsplash.com/photo-1668120089662-42642838cfef?w=1400&q=75&auto=format",
  driveway: "https://customer-assets.emergentagent.com/job_xeriscape-austin-tx/artifacts/jfpm6agi_IMG_0214.jpeg",
  patio: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1400&q=75&auto=format",
  pool: "https://customer-assets.emergentagent.com/job_xeriscape-austin-tx/artifacts/5c1ygdmv_IMG_0216.jpeg",
  firepit: "https://customer-assets.emergentagent.com/job_xeriscape-austin-tx/artifacts/s48sd0ri_IMG_0215.jpeg",
  outdoor: "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1400&q=75&auto=format",
  retaining: "https://images.unsplash.com/photo-1591474200742-8e512e6f98f8?w=1400&q=75&auto=format",
};
